import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from "react";
import { allSpecies, species } from "@redchain/gen1-data";
import "./gen1.css";
import { useGen1Nav } from "./useGen1Nav";
import { BoardBrowser, useBoardBrowser } from "./BoardBrowser";
import type {
  PartySlot,
  TradeRow,
  BattleRow,
  TrainerRow,
} from "../api/cableClub";
export type {
  PartySlot,
  TradeRow,
  BattleRow,
  TrainerRow,
  ClubInvite,
  OutgoingRequest,
} from "../api/cableClub";

export const INVITE_TIMEOUT_MS = 30_000;
type Action = () => void | Promise<void>;
type MenuRow = { label: ReactNode; run: () => void };

function MenuFrame({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="wallet-menu"
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div
        className="wallet-menu__lcd wallet-menu__lcd--full"
        data-screen="cable-club"
      >
        <div className="wallet-menu__box">
          <div className="wallet-menu__content wallet-menu__content--board">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
function ChoiceList({
  items,
  index,
  onPick,
}: {
  items: ReactNode[];
  index: number;
  onPick: (i: number) => void;
}) {
  return (
    <ul className="wallet-menu__list" role="listbox">
      {items.map((label, i) => (
        <li
          key={i}
          role="option"
          aria-selected={i === index}
          onClick={() => onPick(i)}
        >
          <span className="wallet-menu__cursor" aria-hidden="true">
            {i === index ? "\u25b6" : "\u00a0"}
          </span>
          <span className="wallet-menu__label">{label}</span>
        </li>
      ))}
    </ul>
  );
}
function PinnedMenu({
  label,
  rows,
  notice,
  onClose,
  children,
  back = "Back",
  busy = false,
}: {
  label: string;
  rows: MenuRow[];
  notice?: string;
  onClose: () => void;
  children?: ReactNode;
  back?: string;
  busy?: boolean;
}) {
  const scroll = useRef<HTMLDivElement>(null);
  const nav = useGen1Nav(
    rows.length + 1,
    (i) => {
      if (!busy) i === rows.length ? onClose() : rows[i]?.run();
    },
    () => {
      if (!busy) onClose();
    },
  );
  useEffect(() => {
    scroll.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [nav.index]);
  return (
    <MenuFrame label={label}>
      <h2 className="cable-club-board__heading">{label}</h2>
      <div className="cable-club-board__scroll" ref={scroll} aria-busy={busy}>
        {notice && (
          <p className="wallet-menu__status" role="status">
            {notice}
          </p>
        )}
        {busy && <p role="status">Please wait...</p>}
        {children}
        <ChoiceList
          items={rows.map((r) => r.label)}
          index={nav.index}
          onPick={(i) => {
            if (!busy) rows[i]?.run();
          }}
        />
      </div>
      <div className="cable-club-board__close">
        <ChoiceList
          items={[back]}
          index={nav.index === rows.length ? 0 : -1}
          onPick={() => {
            if (!busy) onClose();
          }}
        />
      </div>
    </MenuFrame>
  );
}
function monName(id: number) {
  return species(id)?.name ?? `#${id}`;
}
function monText(mon: PartySlot) {
  return `${monName(mon.species)} Lv${mon.level}`;
}
function levelText(min = 1, max = 100) {
  return min === 1 && max === 100
    ? "Any level"
    : min === max
      ? `Lv${min}`
      : `Lv${min}–${max}`;
}
function wantsText(row: Pick<TradeRow, "wants" | "minLevel" | "maxLevel">) {
  return `${row.wants === "any" ? "Any Pokémon" : monName(row.wants)} · ${levelText(row.minLevel, row.maxLevel)}`;
}
function RowDetails({ row }: { row: TradeRow | BattleRow }) {
  return "species" in row ? (
    <div className="cable-club-board__details">
      <p>Offers: {monText(row)}</p>
      <p>Wants: {wantsText(row)}</p>
      <p>{row.minted ? "Minted Pokémon only" : "Unminted Pokémon only"}</p>
    </div>
  ) : (
    <div className="cable-club-board__details">
      <p>{row.partySize} Pokémon</p>
      <p>Levels: {row.levels.map((l) => `Lv${l}`).join(" / ")}</p>
      <p>Normal game levels. No wager.</p>
    </div>
  );
}
interface BoardProps {
  onClose: () => void;
  requirement?: string;
  notice?: string;
  battles?: BattleRow[];
  myBattles?: BattleRow[];
  trades?: TradeRow[];
  myTrades?: TradeRow[];
  trainers?: TrainerRow[];
  onInvite?: (
    playerId: string,
    kind: "battle" | "trade",
  ) => void | Promise<void>;
  onTakeBattle?: (row: BattleRow) => void | Promise<void>;
  onTakeTrade?: (row: TradeRow, slot: number) => void | Promise<void>;
  onPostBattle?: Action;
  onPostTrade?: (
    slot: number,
    wants: number | "any",
    minLevel?: number,
    maxLevel?: number,
  ) => void | Promise<void>;
  onCancelListing?: (id: string) => void | Promise<void>;
  onReadParty?: () => Promise<PartySlot[]>;
  onRefresh?: Action;
}
type Screen =
  | "browse"
  | "mine"
  | "detail"
  | "cancel"
  | "party"
  | "wants"
  | "species"
  | "levels"
  | "review"
  | "battleReview"
  | "chooseOffer"
  | "requestReview"
  | "trainers"
  | "inviteReview";
export function CableClubBattleBoard(props: BoardProps) {
  return <ClubBoard {...props} kind="battle" />;
}
export function CableClubTradeBoard(props: BoardProps) {
  return <ClubBoard {...props} kind="trade" />;
}
function ClubBoard(props: BoardProps & { kind: "battle" | "trade" }) {
  const { kind, onClose, requirement, notice, onReadParty } = props;
  const [screen, setScreen] = useState<Screen>("browse");
  const [selected, setSelected] = useState<{ id: string; mine: boolean }>();
  const [trainer, setTrainer] = useState<TrainerRow>();
  const [party, setParty] = useState<PartySlot[]>([]);
  const [slot, setSlot] = useState(0);
  const [wants, setWants] = useState<number | "any">("any");
  const [minText, setMinText] = useState("1"),
    [maxText, setMaxText] = useState("100");
  const [search, setSearch] = useState("");
  const [localNotice, setLocalNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const listings =
    kind === "battle" ? (props.battles ?? []) : (props.trades ?? []);
  const mine =
    kind === "battle" ? (props.myBattles ?? []) : (props.myTrades ?? []);
  const browser = useBoardBrowser(screen === "mine", listings, mine);
  const row = (selected?.mine ? mine : listings).find(
    (r) => r.id === selected?.id,
  );
  const minLevel = Number(minText),
    maxLevel = Number(maxText);
  const go = (to: Screen) => {
    setLocalNotice(undefined);
    setScreen(to);
  };
  const perform = async (action: Action, success?: () => void) => {
    if (running.current || requirement) return;
    running.current = true;
    setBusy(true);
    setLocalNotice(undefined);
    try {
      await action();
      success?.();
    } catch (err) {
      setLocalNotice(
        err instanceof Error
          ? err.message
          : "Couldn't complete the request. Try again.",
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  const readParty = (to: Screen) =>
    void perform(async () => {
      const mons = (await onReadParty?.()) ?? [];
      if (!mons.length)
        throw new Error(
          "No verified party. Save your game and wait for verification.",
        );
      setParty(mons);
      go(to);
    });
  const startPost = () => {
    setWants("any");
    setMinText("1");
    setMaxText("100");
    readParty(kind === "battle" ? "battleReview" : "party");
  };
  const show = (
    label: string,
    rows: MenuRow[],
    children?: ReactNode,
    back: () => void = () => go("browse"),
    backLabel = "Back",
  ) => (
    <PinnedMenu
      key={screen}
      label={label}
      rows={requirement ? [] : rows}
      notice={
        requirement ??
        localNotice ??
        (["browse", "mine"].includes(screen) ? notice : undefined)
      }
      onClose={back}
      back={backLabel}
      busy={busy}
    >
      {children}
    </PinnedMenu>
  );
  if ((screen === "browse" || screen === "mine") && browser.filtersOpen) {
    const apply = () => {
      try {
        browser.applyFilters();
        setLocalNotice(undefined);
      } catch (err) {
        setLocalNotice((err as Error).message);
      }
    };
    const fieldKey = (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === "Escape") browser.closeFilters();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.currentTarget.blur();
      }
      if (e.key === "Enter") {
        e.preventDefault();
        apply();
      }
    };
    return show(
      "Filter posts",
      [
        {
          label: "Apply filters",
          run: apply,
        },
        ...(kind === "trade"
          ? [
              {
                label: browser.draft.matches
                  ? "Matches my party: On"
                  : "Matches my party: Off",
                run: () =>
                  browser.setDraft({
                    ...browser.draft,
                    matches: !browser.draft.matches,
                  }),
              },
            ]
          : []),
        { label: "Clear filters", run: browser.clearFilters },
      ],
      <>
        {kind === "trade" && (
          <label className="cable-club-board__field">
            Offered Pokémon
            <input
              aria-label="Offered Pokémon"
              value={browser.draft.species}
              onChange={(e) =>
                browser.setDraft({ ...browser.draft, species: e.target.value })
              }
              onKeyDown={fieldKey}
            />
          </label>
        )}
        <div className="cable-club-board__levels">
          <label className="cable-club-board__field">
            Min level
            <input
              aria-label="Filter minimum level"
              type="number"
              min="1"
              max="100"
              value={browser.draft.min}
              onChange={(e) =>
                browser.setDraft({ ...browser.draft, min: e.target.value })
              }
              onKeyDown={fieldKey}
            />
          </label>
          <label className="cable-club-board__field">
            Max level
            <input
              aria-label="Filter maximum level"
              type="number"
              min="1"
              max="100"
              value={browser.draft.max}
              onChange={(e) =>
                browser.setDraft({ ...browser.draft, max: e.target.value })
              }
              onKeyDown={fieldKey}
            />
          </label>
        </div>
        {kind === "battle" && <p>All party levels must fit this range.</p>}
      </>,
      browser.closeFilters,
    );
  }
  if (screen === "browse" || screen === "mine")
    return (
      <BoardBrowser
        label={kind === "battle" ? "Battle" : "Trade"}
        own={screen === "mine"}
        count={mine.length}
        model={browser}
        post={
          (
            kind === "battle"
              ? props.onPostBattle
              : props.onPostTrade && onReadParty
          )
            ? startPost
            : undefined
        }
        openMine={() => go("mine")}
        openBrowse={() => go("browse")}
        nearby={
          props.trainers?.length && props.onInvite
            ? () => go("trainers")
            : undefined
        }
        refresh={() =>
          void perform(async () => {
            await props.onRefresh?.();
            browser.refresh();
          })
        }
        select={(r) => {
          setSelected({ id: r.id, mine: screen === "mine" });
          go("detail");
        }}
        close={onClose}
        notice={requirement ?? localNotice ?? notice}
        locked={!!requirement || busy}
      />
    );
  if (
    ["detail", "cancel", "chooseOffer", "requestReview"].includes(screen) &&
    !row
  )
    return show(
      "Post unavailable",
      [
        {
          label: "Refresh",
          run: () =>
            void perform(props.onRefresh ?? (() => {}), () => go("browse")),
        },
      ],
      <p>This post was cancelled, taken, or its trainer left.</p>,
    );
  if (screen === "detail" && row) {
    const actions: MenuRow[] = [];
    if (selected?.mine) {
      actions.push({
        label: "Edit post",
        run: () => {
          if ("species" in row) {
            setWants(row.wants);
            setMinText(String(row.minLevel ?? 1));
            setMaxText(String(row.maxLevel ?? 100));
          }
          readParty(kind === "battle" ? "battleReview" : "party");
        },
      });
      if (props.onCancelListing)
        actions.push({ label: "Cancel post", run: () => go("cancel") });
    } else if ("species" in row) {
      if (props.onTakeTrade && row.qualifyingSlots.length)
        actions.push({
          label: "Choose Pokémon",
          run: () => readParty("chooseOffer"),
        });
    } else if (props.onTakeBattle)
      actions.push({
        label: "Review challenge",
        run: () => readParty("requestReview"),
      });
    return show(
      row.trainer,
      actions,
      <>
        <RowDetails row={row} />
        {"species" in row && !selected?.mine && !row.qualifyingSlots.length && (
          <p>
            No matching Pokémon in your verified party. Save if your party
            changed.
          </p>
        )}
      </>,
      () => go(selected?.mine ? "mine" : "browse"),
    );
  }
  if (screen === "cancel" && row)
    return show(
      "Cancel this post?",
      [
        {
          label: "Yes, cancel",
          run: () =>
            void perform(
              () => props.onCancelListing?.(row.id),
              () => go("mine"),
            ),
        },
      ],
      <RowDetails row={row} />,
      () => go("detail"),
      "Keep post",
    );
  if (screen === "party" || screen === "chooseOffer") {
    const choices = party.flatMap((mon, i) => {
      if (
        screen === "chooseOffer" &&
        row &&
        "species" in row &&
        (mon.minted !== row.minted ||
          (row.wants !== "any" && mon.species !== row.wants) ||
          mon.level < (row.minLevel ?? 1) ||
          mon.level > (row.maxLevel ?? 100))
      )
        return [];
      return [
        {
          label: monText(mon),
          run: () => {
            setSlot(i);
            go(screen === "party" ? "wants" : "requestReview");
          },
        },
      ];
    });
    return show(
      screen === "party" ? "Offer Pokémon" : "Your matching Pokémon",
      choices,
      !choices.length ? (
        <p>No matching Pokémon. Refresh the board.</p>
      ) : undefined,
      () => go(screen === "party" ? "browse" : "detail"),
    );
  }
  if (screen === "wants")
    return show(
      "Wanted Pokémon",
      [
        {
          label: "Any Pokémon",
          run: () => {
            setWants("any");
            go("levels");
          },
        },
        {
          label: "Choose species",
          run: () => {
            setSearch("");
            go("species");
          },
        },
      ],
      <p>You offer {party[slot] && monText(party[slot]!)}</p>,
      () => go("party"),
    );
  if (screen === "species") {
    const results = allSpecies().filter((m) =>
      m.name.toLowerCase().includes(search.toLowerCase()),
    );
    return show(
      "Choose species",
      results.map((m) => ({
        label: m.name,
        run: () => {
          setWants(m.index);
          go("levels");
        },
      })),
      <label className="cable-club-board__field">
        Search species
        <input
          aria-label="Search species"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Escape") go("wants");
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Enter" && results[0]) {
              e.preventDefault();
              setWants(results[0].index);
              go("levels");
            }
          }}
        />
      </label>,
      () => go("wants"),
    );
  }
  if (screen === "levels") {
    const review = () => {
      if (
        !Number.isInteger(minLevel) ||
        !Number.isInteger(maxLevel) ||
        minLevel < 1 ||
        maxLevel > 100 ||
        minLevel > maxLevel
      ) {
        setLocalNotice(
          "Minimum and maximum must be 1–100, with minimum no higher than maximum.",
        );
        return;
      }
      go("review");
    };
    const key = (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        review();
      }
      if (e.key === "Escape") go("wants");
    };
    return show(
      "Wanted level",
      [
        { label: "Review post", run: review },
        {
          label: "Any level",
          run: () => {
            setMinText("1");
            setMaxText("100");
            go("review");
          },
        },
      ],
      <>
        <p>{wants === "any" ? "Any Pokémon" : monName(wants)}</p>
        <div className="cable-club-board__levels">
          <label className="cable-club-board__field">
            Min
            <input
              aria-label="Minimum level"
              type="number"
              min="1"
              max="100"
              value={minText}
              onChange={(e) => setMinText(e.target.value)}
              onKeyDown={key}
            />
          </label>
          <label className="cable-club-board__field">
            Max
            <input
              aria-label="Maximum level"
              type="number"
              min="1"
              max="100"
              value={maxText}
              onChange={(e) => setMaxText(e.target.value)}
              onKeyDown={key}
            />
          </label>
        </div>
      </>,
      () => go("wants"),
    );
  }
  if (screen === "review")
    return show(
      "Review trade",
      [
        {
          label: "Confirm post",
          run: () =>
            void perform(
              () => props.onPostTrade?.(slot, wants, minLevel, maxLevel),
              () => go("mine"),
            ),
        },
      ],
      <div className="cable-club-board__details">
        <p>Offer: {party[slot] && monText(party[slot]!)}</p>
        <p>Want: {wantsText({ wants, minLevel, maxLevel })}</p>
        <p>
          Stays posted while you are in the Club. Replaces your previous trade
          post.
        </p>
      </div>,
      () => go("levels"),
    );
  if (screen === "battleReview")
    return show(
      "Review battle post",
      [
        {
          label: "Confirm post",
          run: () =>
            void perform(
              () => props.onPostBattle?.(),
              () => go("mine"),
            ),
        },
      ],
      <>
        <RowDetails
          row={{
            id: "preview",
            trainer: "You",
            partySize: party.length,
            levels: party.map((m) => m.level),
          }}
        />
        <p>
          Others request; you accept before connecting. Replaces your previous
          battle post.
        </p>
      </>,
      () => go("browse"),
    );
  if (screen === "requestReview" && row)
    return show(
      "Review request",
      [
        {
          label: "Send request",
          run: () =>
            void perform(
              () =>
                "species" in row
                  ? props.onTakeTrade?.(row, slot)
                  : props.onTakeBattle?.(row),
              () => go("browse"),
            ),
        },
      ],
      <>
        <p>To: {row.trainer}</p>
        <RowDetails row={row} />
        <p>
          {"species" in row
            ? `You offer: ${party[slot] ? monText(party[slot]!) : ""}`
            : `Your levels: ${party.map((m) => `Lv${m.level}`).join(" / ")}`}
        </p>
        <p>
          Wait for acceptance.{" "}
          {kind === "trade"
            ? "Confirm the actual exchange in-game."
            : "Both trainers connect when accepted."}
        </p>
      </>,
      () => go("detail"),
    );
  if (screen === "trainers")
    return show(
      "Nearby trainers",
      (props.trainers ?? []).map((t) => ({
        label: t.trainer,
        run: () => {
          setTrainer(t);
          go("inviteReview");
        },
      })),
    );
  return show(
    "Send invitation?",
    trainer
      ? [
          {
            label: "Send request",
            run: () =>
              void perform(
                () => props.onInvite?.(trainer.playerId, kind),
                () => go("browse"),
              ),
          },
        ]
      : [],
    <p>
      Invite {trainer?.trainer} to {kind}?{" "}
      {kind === "trade"
        ? "Choose Pokémon together in the Trade Center."
        : "Normal game levels. No wager."}
    </p>,
    () => go("trainers"),
  );
}
export function armInviteTimeout(onDecline: () => void): () => void {
  const timer = setTimeout(onDecline, INVITE_TIMEOUT_MS);
  return () => clearTimeout(timer);
}
export function CableClubInvite({
  name,
  kind,
  onDecline,
  onAccept,
  onPause,
  offer,
  requested,
  levels,
  notice,
}: {
  name: string;
  kind: "battle" | "trade";
  onDecline: () => void;
  onAccept?: Action;
  onPause?: () => void;
  offer?: PartySlot;
  requested?: PartySlot;
  levels?: number[];
  notice?: string;
}) {
  const callbacks = useRef({ onPause, onDecline });
  callbacks.current = { onPause, onDecline };
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string>();
  const accepting = useRef(false);
  useEffect(() => {
    callbacks.current.onPause?.();
    return armInviteTimeout(() => {
      if (!accepting.current) callbacks.current.onDecline();
    });
  }, [name, kind]);
  const accept = async () => {
    if (accepting.current) return;
    accepting.current = true;
    setBusy(true);
    try {
      await onAccept?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't accept.");
    } finally {
      accepting.current = false;
      setBusy(false);
    }
  };
  return (
    <PinnedMenu
      label="Cable Club request"
      notice={error ?? notice}
      busy={busy}
      rows={onAccept ? [{ label: "Accept", run: () => void accept() }] : []}
      onClose={onDecline}
      back="Decline"
    >
      <p>
        {name} wants to {kind}. Accept?
      </p>
      {offer && <p>They offer: {monText(offer)}</p>}
      {requested && <p>You offer: {monText(requested)}</p>}
      {levels && kind === "battle" && (
        <p>Their levels: {levels.map((l) => `Lv${l}`).join(" / ")}</p>
      )}
      <p>
        {kind === "trade"
          ? "Review and confirm the actual exchange in-game."
          : "Normal game levels. No wager."}
      </p>
    </PinnedMenu>
  );
}
export function CableClubWaiting({
  trainer,
  onCancel,
  connecting = false,
}: {
  trainer: string;
  onCancel: Action;
  connecting?: boolean;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string>();
  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't cancel.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <PinnedMenu
      label={connecting ? "Connecting" : "Waiting for trainer"}
      rows={[]}
      notice={error}
      onClose={() => void cancel()}
      back={connecting ? "Connecting..." : "Cancel request"}
      busy={busy || connecting}
    >
      <p>Request sent to {trainer}.</p>
      <p>
        {connecting
          ? "Both trainers are ready. Creating your connection..."
          : "Waiting for acceptance. Requests expire after 30 seconds."}
      </p>
    </PinnedMenu>
  );
}
